import { useRef, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { useHome } from "@/context/HomeContent";
import { useWhatsAppHref } from "@/lib/whatsapp";
import { mediaUrl } from "@/lib/api";
import { maskLine, easeSoft } from "@/lib/animations";

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0.4 }),
  center: { x: "0%", opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0.4 }),
};

const Hero = () => {
  const { home } = useHome();
  const waHref = useWhatsAppHref();
  const d = home.hero;
  const HEADLINE = d.headline || [];
  const SLIDES = (d.slides || []).map((s) => ({ ...s, src: mediaUrl(s.src) }));

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const [[index, dir], setSlide] = useState([0, 0]);
  const [paused, setPaused] = useState(false);
  const n = SLIDES.length || 1;
  const active = ((index % n) + n) % n;
  const paginate = useCallback((delta) => setSlide(([i]) => [i + delta, delta]), []);
  const goTo = useCallback((i) => setSlide(([cur]) => [i, i > (((cur % n) + n) % n) ? 1 : -1]), [n]);

  useEffect(() => {
    if (paused || n <= 1) return;
    const t = setInterval(() => setSlide(([i]) => [i + 1, 1]), 4200);
    return () => clearInterval(t);
  }, [paused, index, n]);

  const slide = SLIDES[active] || {};

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-brand-cream pt-32 sm:pt-36 lg:pt-40">
      <div className="pointer-events-none absolute -left-40 top-10 h-[32rem] w-[32rem] rounded-full bg-brand-magenta/10 blur-[120px]" />
      <div className="pointer-events-none absolute -right-40 top-40 h-[32rem] w-[32rem] rounded-full bg-brand-blue/10 blur-[120px]" />

      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
        <div className="flex flex-col justify-center pb-6 lg:pb-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: easeSoft }} className="mb-7 inline-flex w-fit items-center gap-2 rounded-full border border-brand-line bg-white/60 px-4 py-2 backdrop-blur" data-testid="hero-badge">
            <Sparkles className="h-3.5 w-3.5 text-brand-magenta" />
            <span className="font-heading text-xs font-semibold uppercase tracking-[0.18em] text-brand-ink/70">{d.badge}</span>
          </motion.div>

          <h1 className="font-heading text-[2.6rem] font-semibold leading-[0.95] tracking-tight text-brand-ink sm:text-6xl lg:text-[clamp(3rem,4.6vw,4.35rem)] lg:leading-[0.92]">
            {HEADLINE.map((line, i) => (
              <span key={i} className="block overflow-hidden pt-[0.06em] pb-[0.2em] -mb-[0.14em]">
                <motion.span className={`block ${line.grad ? "brand-gradient-text" : ""}`} variants={maskLine} initial="hidden" animate="show" custom={i}>
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: easeSoft, delay: 0.9 }} className="mt-7 max-w-xl font-body text-base leading-relaxed text-brand-ink/70 sm:text-lg" data-testid="hero-subheadline">
            {d.subheadline}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: easeSoft, delay: 1.05 }} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a href={d.ctaPrimary?.href} data-testid="hero-cta-primary" className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-magenta px-7 py-4 font-body text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.04]">
              {d.ctaPrimary?.label}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-secondary" className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-ink/25 bg-transparent px-7 py-4 font-body text-sm font-semibold text-brand-ink transition-colors duration-300 hover:border-brand-ink hover:bg-brand-ink hover:text-white">
              {d.ctaSecondary?.label}
            </a>
          </motion.div>
        </div>

        <div className="relative flex items-end">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: easeSoft, delay: 0.4 }} style={{ y: imgY }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} className="group relative h-[440px] w-full overflow-hidden rounded-t-[180px] border border-brand-line bg-brand-ink sm:h-[540px] lg:h-[660px]" data-testid="hero-slider">
            <AnimatePresence initial={false} custom={dir} mode="popLayout">
              <motion.div key={active} custom={dir} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ x: { duration: 0.8, ease: easeSoft }, opacity: { duration: 0.5 } }} className="absolute inset-0" data-testid={`hero-slide-${active}`}>
                <motion.img src={slide.src} alt={slide.label} style={{ scale: imgScale }} className="h-full w-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-brand-ink/10 to-transparent" />
              </motion.div>
            </AnimatePresence>

            <div className="absolute bottom-8 left-8 right-8 z-10 flex items-end justify-between">
              <AnimatePresence mode="wait">
                <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.5, ease: easeSoft }}>
                  <p className="font-heading text-lg font-semibold text-white sm:text-xl">{slide.label}</p>
                  <p className="font-body text-xs uppercase tracking-[0.18em] text-white/60">{slide.place}</p>
                </motion.div>
              </AnimatePresence>
              <span className="font-heading text-sm font-semibold text-white/70">0{active + 1}<span className="text-white/40"> / 0{n}</span></span>
            </div>

            <button type="button" aria-label="Previous slide" data-testid="hero-slider-prev" onClick={() => paginate(-1)} className="absolute left-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-brand-ink/30 text-white opacity-0 backdrop-blur transition-[opacity,background-color] duration-300 hover:bg-brand-magenta group-hover:opacity-100"><ChevronLeft className="h-5 w-5" /></button>
            <button type="button" aria-label="Next slide" data-testid="hero-slider-next" onClick={() => paginate(1)} className="absolute right-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-brand-ink/30 text-white opacity-0 backdrop-blur transition-[opacity,background-color] duration-300 hover:bg-brand-magenta group-hover:opacity-100"><ChevronRight className="h-5 w-5" /></button>

            <div className="absolute right-8 top-8 z-10 flex gap-2">
              {SLIDES.map((_, i) => (
                <button key={i} type="button" aria-label={`Go to slide ${i + 1}`} data-testid={`hero-slider-dot-${i}`} onClick={() => goTo(i)} className={`h-1.5 rounded-full transition-all duration-500 ${i === active ? "w-8 bg-white" : "w-1.5 bg-white/45 hover:bg-white/70"}`} />
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.2 }} className="relative mt-6 border-y border-brand-line bg-white/50 backdrop-blur" data-testid="hero-credibility-strip">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 divide-y divide-brand-line px-5 sm:grid-cols-3 sm:divide-x sm:divide-y-0 sm:px-8">
          {(d.credibility || []).map((c) => (
            <div key={c.label} className="flex items-baseline gap-3 py-5 sm:justify-center">
              <span className="font-heading text-2xl font-bold text-brand-ink sm:text-3xl">{c.value}</span>
              <span className="font-body text-sm text-brand-ink/60">{c.label}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
